import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../components/Navbar";
import Loading from "../../components/Loading";
import SpacesService from "../../services/providers/SpacesService";
import toast from "react-hot-toast";
import ConfirmModal from "./components/ConfirmModal";
import TokenInputModal from "./components/TokenInputModal";
import "../../styles/global.css";

const SpacesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [spaces, setSpaces] = useState([]);
  const [requestingToken, setRequestingToken] = useState(false);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: null,
    variant: "default",
  });
  const [tokenModal, setTokenModal] = useState({
    isOpen: false,
    spaceId: null,
    spaceName: "",
    expiresAt: null,
    error: null,
  });

  useEffect(() => {
    fetchSpaces();
  }, []);

  const fetchSpaces = async () => {
    try {
      const data = await SpacesService.listSpaces();
      setSpaces(data || []);
    } catch (error) {
      console.error("Error fetching spaces:", error);
      toast.error("Failed to load spaces");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (spaceId, spaceName) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Space",
      message: `Are you sure you want to delete "${spaceName}"? This action cannot be undone and all files in this Space will be permanently deleted. A verification code will be sent to your email address.`,
      onConfirm: async () => {
        // Close confirmation modal
        setConfirmModal({
          isOpen: false,
          title: "",
          message: "",
          onConfirm: null,
          variant: "default",
        });

        // Request deletion token
        await requestDeletionToken(spaceId, spaceName);
      },
      variant: "danger",
    });
  };

  const requestDeletionToken = async (spaceId, spaceName) => {
    setRequestingToken(true);
    try {
      const response = await SpacesService.requestDeletionToken(spaceId);

      // Open token input modal
      setTokenModal({
        isOpen: true,
        spaceId,
        spaceName,
        expiresAt: response.expiresAt,
        error: null,
      });

      toast.success("Verification code sent to your email");

      // In development, show token in console
      if (response.devToken) {
        console.log("Dev token:", response.devToken);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send verification code. Please try again.";

      // Check if token already exists - open modal directly so user can enter existing token
      if (error.response?.status === 429 && error.response?.data?.tokenExists) {
        const { expiresAt, minutesRemaining } = error.response.data;
        // Open token modal directly - user can enter the existing token
        setTokenModal({
          isOpen: true,
          spaceId,
          spaceName,
          expiresAt,
          error: null, // No error - just let them enter the token
        });
        // Show info toast instead of error
        toast.success(
          `A verification code was already sent. Please enter the code from your email. It expires in ${minutesRemaining} minute(s).`,
          { duration: 5000 }
        );
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setRequestingToken(false);
    }
  };

  const handleTokenSubmit = async (token) => {
    if (!token || token.length !== 8) {
      setTokenModal((prev) => ({
        ...prev,
        error: "Please enter a valid 8-character code",
      }));
      return;
    }

    setVerifyingToken(true);
    setTokenModal((prev) => ({ ...prev, error: null }));

    try {
      const spaceId = tokenModal.spaceId;

      // Verify token and delete space
      const deleteToast = toast.loading("Verifying code and deleting space...");

      // Delete space with token (backend will verify token)
      await SpacesService.deleteSpace(spaceId, token);

      toast.dismiss(deleteToast);
      toast.success("Space deleted successfully");

      // Close token modal
      setTokenModal({
        isOpen: false,
        spaceId: null,
        spaceName: "",
        expiresAt: null,
        error: null,
      });

      // Refresh spaces list
      fetchSpaces();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Invalid code or failed to delete space. Please try again.";

      setTokenModal((prev) => ({
        ...prev,
        error: errorMessage,
      }));

      toast.error(errorMessage);
    } finally {
      setVerifyingToken(false);
    }
  };

  const handleResendToken = async () => {
    if (!tokenModal.spaceId) return;

    setRequestingToken(true);
    try {
      const response = await SpacesService.requestDeletionToken(
        tokenModal.spaceId
      );

      setTokenModal((prev) => ({
        ...prev,
        expiresAt: response.expiresAt,
        error: null,
      }));

      toast.success("New verification code sent to your email");

      if (response.devToken) {
        console.log("Dev token:", response.devToken);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to resend code. Please try again.";

      if (error.response?.status === 429) {
        setTokenModal((prev) => ({
          ...prev,
          error: errorMessage,
        }));
      }

      toast.error(errorMessage);
    } finally {
      setRequestingToken(false);
    }
  };

  if (loading) return <Loading />;

  return (
    <>
      <AppNavbar />
      <div className="spaces-page main-container">
        <div className="container">
          {/* Page Header */}
          <div className="spaces-page-header">
            <div className="spaces-page-header-content">
              <h1 className="page-title">Spaces</h1>
              <p className="page-subtitle">
                S3-compatible object storage for your applications. Store and
                serve files at scale.
              </p>
            </div>
            <button
              className="btn-primary btn-cta"
              onClick={() => navigate("/spaces/order")}
            >
              + Create Space
            </button>
          </div>

          {/* Empty State */}
          {spaces.length === 0 ? (
            <div className="spaces-empty-state">
              <div className="spaces-empty-icon">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
              <h2 className="spaces-empty-title">No Spaces yet</h2>
              <p className="spaces-empty-desc">
                Get started by creating your first Space. Spaces provide
                scalable object storage with a simple API, perfect for storing
                images, videos, backups, and more.
              </p>
              <button
                className="btn-secondary"
                onClick={() => navigate("/spaces/order")}
              >
                + Create your first Space
              </button>
            </div>
          ) : (
            <>
              {/* Spaces Grid */}
              <div className="spaces-grid">
                {spaces.map((space) => (
                  <div
                    key={space.db_id || space.id}
                    className="space-card"
                    onClick={() => navigate(`/spaces/${space.name}`)}
                  >
                    {/* Card Header */}
                    <div className="space-card-header">
                      <div className="space-card-icon">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                      <div className="space-card-status">
                        <span
                          className={`status-dot ${
                            space.status === "active" ? "active" : "inactive"
                          }`}
                        ></span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="space-card-content">
                      <h3 className="space-card-title">{space.name}</h3>
                      <div className="space-card-meta">
                        <div className="space-card-meta-item">
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                          </svg>
                          <span>{space.region}</span>
                        </div>
                        <div className="space-card-meta-item">
                          <span
                            className={`badge ${
                              space.status === "active"
                                ? "badge-success"
                                : "badge-error"
                            }`}
                          >
                            {space.status || "active"}
                          </span>
                        </div>
                      </div>

                      {space.endpoint && (
                        <div className="space-card-endpoint">
                          <div className="space-card-endpoint-label">
                            Endpoint
                          </div>
                          <div className="space-card-endpoint-value">
                            {space.endpoint}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div
                      className="space-card-footer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="space-card-action"
                        onClick={() => navigate(`/spaces/${space.name}`)}
                        disabled={space.status === "suspended"}
                      >
                        <span>View Details</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                      <button
                        className="space-card-action-danger"
                        onClick={() =>
                          handleDelete(space.db_id || space.id, space.name)
                        }
                        disabled={space.status === "suspended"}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => {
          if (!requestingToken) {
            setConfirmModal({
              isOpen: false,
              title: "",
              message: "",
              onConfirm: null,
              variant: "default",
            });
          }
        }}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={requestingToken ? "Sending code..." : "Continue"}
        cancelText="Cancel"
        variant={confirmModal.variant}
        loading={requestingToken}
      />

      {/* Token Input Modal */}
      <TokenInputModal
        isOpen={tokenModal.isOpen}
        onClose={() => {
          if (!verifyingToken && !requestingToken) {
            setTokenModal({
              isOpen: false,
              spaceId: null,
              spaceName: "",
              expiresAt: null,
              error: null,
            });
          }
        }}
        onConfirm={handleTokenSubmit}
        title="Verify Deletion"
        message={`Please enter the 8-character verification code sent to your email to confirm deletion of "${tokenModal.spaceName}".`}
        spaceName={tokenModal.spaceName}
        confirmText="Verify & Delete"
        cancelText="Cancel"
        loading={verifyingToken}
        error={tokenModal.error}
        onResendToken={handleResendToken}
        resendingToken={requestingToken}
        expiresAt={tokenModal.expiresAt}
      />
    </>
  );
};

export default SpacesDashboard;
